import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";

// Polopatistické porovnania pre laikov — prepojené s detailnými stránkami /materialy a /omietky
const CONTENT = {
  insulation: {
    title: "Ktorá izolácia je pre vás?",
    link: { to: "/materialy", label: "Detailné porovnanie materiálov →" },
    items: [
      ["EPS biely", "Najlacnejší základ. Ľahký, spoľahlivý — používa sa na 8 z 10 domov."],
      ["EPS grafitový (šedý)", "O pätinu lepšie izoluje ako biely — pri rovnakej hrúbke viac ušetríte za kúrenie."],
      ["Minerálna vata", "Nehorí a tlumí hluk. Drahšia, ale najlepšia voľba pre bytovky a rušné ulice."],
      ["XPS", "Najtvrdší a nepúšťa vodu — ideálny do sokla a vlhkých miest, nie na celú fasádu."],
    ],
  },
  thickness: {
    title: "Aká hrúbka sa oplatí?",
    items: [
      ["10 cm", "Minimum podľa normy — vhodné pri tesnom rozpočte na staršom tehlovom dome."],
      ["14–16 cm", "Dnešný štandard. Každé +2 cm ≈ o 5–8 % nižšie náklady na kúrenie."],
      ["18–20 cm", "Pre novostavby a rekonštrukcie s dotáciou — maximálna úspora do budúcna."],
      ["Na čo si dať pozor", "Väčšia hrúbka znamená aj väčšie parapety a dlhšie kotvy — preto cena rastie trochu rýchlejšie."],
    ],
  },
  plaster: {
    title: "Ktorá omietka je pre vás?",
    link: { to: "/omietky", label: "Detailné porovnanie omietok →" },
    items: [
      ["Minerálna", "Najlacnejšia, ale treba ju natrieť farbou a citlivejšie znáša vlhko."],
      ["Akrylátová", "Pružná a drží farbu. Slabšie dýcha — hodí sa na polystyrén."],
      ["Silikónová", "Najčastejšia voľba — voda steká dolu, špina a riasy sa nechyťajú."],
      ["Silikátová", "Prémiová voľba na minerálnu vatu — výborne dýcha a dlho vydrží."],
    ],
  },
  soffit: {
    title: "Ako urobiť podhľad?",
    items: [
      ["Laty každých 25 cm", "Najlacnejšie — dosky sa pripevnia na drevené laty. Stačí, keď je krov rovný."],
      ["Plné debnenie z OSB", "Celá plocha z OSB dosiek — najdrahšie, ale najpevnejšie a najčistejšie."],
      ["Koníky", "Nastaviteľné koníky vyrovnajú nerovnosti — netreba rovnať väzníky. Rýchle a čisté."],
    ],
  },
};

export default function InfoTip({ topic, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const c = CONTENT[topic];
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (!c) return null;
  return (
    <span className="relative inline-block align-middle" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid || `info-tip-${topic}`}
        aria-label={`Vysvetlenie: ${c.title}`}
        className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full border border-gold/50 text-gold hover:bg-gold hover:text-ink transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          data-testid={`info-popover-${topic}`}
          className="absolute z-50 left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-7 w-72 sm:w-80 bg-ink-2 border border-gold/30 shadow-2xl p-4 text-left"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <span className="text-gold font-semibold text-sm">{c.title}</span>
            <button type="button" onClick={() => setOpen(false)} className="text-white/40 hover:text-white" data-testid={`info-close-${topic}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="space-y-2.5">
            {c.items.map(([h, t]) => (
              <li key={h} className="text-xs leading-relaxed">
                <span className="text-white font-medium">{h}: </span>
                <span className="text-white/60">{t}</span>
              </li>
            ))}
          </ul>
          {c.link && (
            <Link to={c.link.to} target="_blank" className="block mt-3 pt-3 border-t border-white/10 text-gold text-xs hover:underline">
              {c.link.label}
            </Link>
          )}
        </div>
      )}
    </span>
  );
}
